<template>
    <BaseDialog
        :showDialog="showDialog"
        :headline="$t('lang_PUBLISHING_STATEMENTS_CAPTION_BANK_FILE_DIALOG_TITLE')"
        :close="closeDialog"
        :alvqa="alvqa"
        maxCardHeight="600"
        maxDialogWidth="600"
    >
        <template v-slot:dialogText>
            <v-container fluid>
                <v-row v-if="!debtor">
                    <v-col class="text-center py-6">
                        <v-progress-circular indeterminate color="dialogProgressbar" />
                    </v-col>
                </v-row>

                <template v-else>
                    <v-row v-if="submitError">
                        <v-col :alvqa="alvqa + '-submit-error'">
                            <v-alert
                                type="error"
                                outlined
                                dense
                                :alvqa="alvqa + '-submit-error-alert'"
                            >
                                {{ $t(submitError.errorMsg) }}
                                <div
                                    v-if="submitError.skipped && submitError.skipped.length > 0"
                                    class="mt-2"
                                    :alvqa="alvqa + '-submit-error-skipped-list'"
                                >
                                    <ul class="pl-4 mb-0">
                                        <li
                                            v-for="item in submitError.skipped"
                                            :key="item.alvId"
                                        >
                                            <router-link
                                                v-if="$alvPlugin.createUrlForAlvId(item.alvId)"
                                                :to="$alvPlugin.createUrlForAlvId(item.alvId)"
                                                target="_blank"
                                                :alvqa="alvqa + '-submit-error-skipped-link-' + item.alvId"
                                            >
                                                {{ item.name }} ({{ item.alvId }})</router-link><span v-else>{{ item.name }} ({{ item.alvId }})</span><span v-if="item.reason">: {{ $t(item.reason) }}</span>
                                        </li>
                                    </ul>
                                </div>
                                <div
                                    v-if="submitError.selfTransferConflicts && submitError.selfTransferConflicts.length > 0"
                                    class="mt-2"
                                    :alvqa="alvqa + '-submit-error-self-transfer-list'"
                                >
                                    <ul class="pl-4 mb-0">
                                        <li
                                            v-for="item in submitError.selfTransferConflicts"
                                            :key="item.alvId"
                                        >
                                            <router-link
                                                v-if="$alvPlugin.createUrlForAlvId(item.alvId)"
                                                :to="$alvPlugin.createUrlForAlvId(item.alvId)"
                                                target="_blank"
                                                :alvqa="alvqa + '-submit-error-self-transfer-link-' + item.alvId"
                                            >
                                                {{ item.name }} ({{ item.alvId }}) — IBAN: {{ item.iban }}
                                            </router-link>
                                            <span v-else>{{ item.name }} ({{ item.alvId }}) — IBAN: {{ item.iban }}</span>
                                        </li>
                                    </ul>
                                </div>
                                <div
                                    v-if="submitError.accountHolderConflicts && submitError.accountHolderConflicts.length > 0"
                                    class="mt-2"
                                    :alvqa="alvqa + '-submit-error-account-holder-list'"
                                >
                                    <ul class="pl-4 mb-0">
                                        <li
                                            v-for="item in submitError.accountHolderConflicts"
                                            :key="item.alvId"
                                        >
                                            <router-link
                                                v-if="$alvPlugin.createUrlForAlvId(item.alvId)"
                                                :to="$alvPlugin.createUrlForAlvId(item.alvId)"
                                                target="_blank"
                                                :alvqa="alvqa + '-submit-error-account-holder-link-' + item.alvId"
                                            >
                                                {{ item.name }} ({{ item.alvId }})
                                            </router-link>
                                            <span v-else>{{ item.name }} ({{ item.alvId }})</span>
                                        </li>
                                    </ul>
                                </div>
                                <div
                                    v-if="submitError.currencyConflicts && submitError.currencyConflicts.length > 0"
                                    class="mt-2"
                                    :alvqa="alvqa + '-submit-error-currency-list'"
                                >
                                    <ul class="pl-4 mb-0">
                                        <li
                                            v-for="item in submitError.currencyConflicts"
                                            :key="item.alvId"
                                        >
                                            <router-link
                                                v-if="$alvPlugin.createUrlForAlvId(item.alvId)"
                                                :to="$alvPlugin.createUrlForAlvId(item.alvId)"
                                                target="_blank"
                                                :alvqa="alvqa + '-submit-error-currency-link-' + item.alvId"
                                            >
                                                {{ item.name }} ({{ item.alvId }})
                                            </router-link>
                                            <span v-else>{{ item.name }} ({{ item.alvId }})</span>
                                        </li>
                                    </ul>
                                </div>
                            </v-alert>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-select
                                v-model="bankFileType"
                                :items="bankFileTypeItems"
                                :label="$t('lang_PUBLISHING_STATEMENTS_CAPTION_BANK_FILE_TYPE')"
                                item-text="text"
                                item-value="value"
                                clearable
                                :alvqa="alvqa + '-format-dropdown'"
                            />
                        </v-col>
                    </v-row>

                    <v-row v-if="bankFileType && !debtor.complete">
                        <v-col :alvqa="alvqa + '-bank-data-missing'">
                            <v-alert
                                type="error"
                                dense
                                text
                                :alvqa="alvqa + '-bank-data-missing-alert'"
                            >
                                {{ debtorBankDataWarningText }}
                                <div v-if="showDebtorAzDeepLink" class="mt-2">
                                    <router-link
                                        :to="$alvPlugin.createUrlForAlvId(debtor.azAlvId)"
                                        target="_blank"
                                        :alvqa="alvqa + '-bank-data-missing-link'"
                                    >
                                        {{ $t('lang_PUBLISHING_STATEMENTS_CAPTION_OPEN_DEBTOR_AZ_ENTRY') }}
                                    </router-link>
                                </div>
                            </v-alert>
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <v-menu
                                ref="executionDateMenu"
                                v-model="executionDateMenuOpen"
                                :close-on-content-click="false"
                                :return-value.sync="executionDate"
                                transition="scale-transition"
                                offset-y
                                min-width="auto"
                            >
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field
                                        :value="formattedExecutionDate"
                                        :label="$t('lang_PUBLISHING_STATEMENTS_CAPTION_BANK_FILE_EXECUTION_DATE')"
                                        append-icon="event"
                                        readonly
                                        v-bind="attrs"
                                        v-on="on"
                                        :alvqa="alvqa + '-execution-date-textfield'"
                                    />
                                </template>
                                <v-date-picker
                                    v-model="executionDate"
                                    :locale="$i18n.locale"
                                    no-title
                                    color="buttonStandard"
                                    :min="minExecutionDate"
                                    :alvqa="alvqa + '-execution-date-picker'"
                                >
                                    <v-spacer />
                                    <BaseButton
                                        class="text-center"
                                        label="lang_GENERAL_BUTTON_CANCEL"
                                        @click="executionDateMenuOpen = false"
                                        :alvqa="alvqa + '-execution-date-cancel'"
                                    />
                                    <BaseButton
                                        class="text-center"
                                        label="lang_GENERAL_BUTTON_OK"
                                        @click="$refs.executionDateMenu.save(executionDate)"
                                        :alvqa="alvqa + '-execution-date-ok'"
                                    />
                                </v-date-picker>
                            </v-menu>
                        </v-col>
                    </v-row>
                </template>
            </v-container>
        </template>

        <template v-slot:dialogActions>
            <v-container fluid>
                <v-row>
                    <v-spacer />
                    <v-col class="d-flex justify-end pl-0 pt-0" align-self="center">
                        <v-progress-circular
                            v-if="isSubmitting"
                            class="mr-5"
                            :alvqa="alvqa + '-progress-bar'"
                            indeterminate
                            color="dialogProgressbar"
                        />
                        <BaseButton
                            label="lang_BUTTON_START"
                            :disabled="!canSubmit"
                            @click="submit"
                            :alvqa="alvqa + '-button-submit'"
                        />
                    </v-col>
                </v-row>
            </v-container>
        </template>
    </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/_base/BaseDialog'
import BaseButton from '@/components/_buttons/BaseButton'

export default {
    name: 'PublishingStatementsBankFileDialog',

    components: {
        BaseDialog,
        BaseButton
    },

    data() {
        return {
            alvqa: 'publishing-statements-bank-file-dialog',
            executionDateMenuOpen: false
        }
    },

    computed: {
        showDialog: {
            get() { return this.$store.getters.getShowPublishingStatementsBankFileDialog },
            set(value) { this.$store.commit('setShowPublishingStatementsBankFileDialog', value) }
        },
        debtor() {
            return this.$store.getters.getPublishingStatementsBankFileDialogDebtor
        },
        bankFileType: {
            get() { return this.$store.getters.getPublishingStatementsBankFileDialogType },
            set(value) { this.$store.commit('setPublishingStatementsBankFileDialogType', value) }
        },
        executionDate: {
            get() { return this.$store.getters.getPublishingStatementsBankFileDialogExecutionDate },
            set(value) { this.$store.commit('setPublishingStatementsBankFileDialogExecutionDate', value) }
        },
        isSubmitting() {
            return this.$store.getters.getPublishingStatementsBankFileDialogIsSubmitting
        },
        submitError() {
            return this.$store.getters.getPublishingStatementsBankFileDialogError
        },
        bankFileTypeItems() {
            if (!this.debtor) return []
            // Items always selectable - the per-format bank-data check (and the
            // submit button) decide whether the user can actually proceed. This
            // way the user can pick a format first and see exactly which fields
            // are missing for that specific format, instead of being blocked at
            // the dialog entry without context.
            return [
                {
                    text: this.$t('lang_PUBLISHING_STATEMENTS_DROPDOWN_BANK_FILE_TYPE_SEPA_PAIN_001_001_09'),
                    value: 'sepa',
                    alvqa: this.alvqa + '-format-option-sepa'
                },
                {
                    text: this.$t('lang_PUBLISHING_STATEMENTS_DROPDOWN_BANK_FILE_TYPE_FOREIGN_PAIN_001_001_09'),
                    value: 'foreign',
                    alvqa: this.alvqa + '-format-option-foreign'
                }
            ]
        },
        formattedExecutionDate() {
            return this.executionDate
                ? this.$d(new Date(this.executionDate), 'alvDateFormat')
                : ''
        },
        minExecutionDate() {
            let today = new Date()
            let yyyy = today.getFullYear()
            let mm = String(today.getMonth() + 1).padStart(2, '0')
            let dd = String(today.getDate()).padStart(2, '0')
            return yyyy + '-' + mm + '-' + dd
        },
        debtorBankDataWarningText() {
            if (!this.debtor || this.debtor.complete) return ''
            const key = this.debtor.warningMessageKey
                || 'lang_PUBLISHING_STATEMENTS_MESSAGE_ERROR_DEBTOR_BANK_DATA_MISSING'
            return this.$t(key)
        },
        showDebtorAzDeepLink() {
            if (!this.debtor || this.debtor.complete) return false
            if (!this.debtor.azAlvId) return false
            const id = Number(this.debtor.azId)
            return id > 0
        },
        canSubmit() {
            return !!this.debtor
                && this.debtor.complete
                && !!this.bankFileType
                && !!this.executionDate
                && !this.isSubmitting
        }
    },

    beforeDestroy() {
        // Wipe dialog state when the page (and therefore this dialog) is unmounted,
        // otherwise navigating away and back to publishing/statements would re-mount
        // the dialog with the previous showDialog=true and stale debtor/error/format.
        this.$store.commit('resetPublishingStatementsBankFileDialog')
    },

    methods: {
        closeDialog() {
            this.$store.commit('resetPublishingStatementsBankFileDialog')
        },
        async submit() {
            if (!this.canSubmit) return
            await this.$store.dispatch('submitPublishingStatementsBankFileGeneration')
        }
    }
}
</script>
